/**
 * Utility to convert full-size Bulbapedia image URLs to thumbnails.
 *
 * Full-size pack art images from archives.bulbagarden.net are 1.5-1.7 MB
 * PNGs. Loading 9+ of them on the browse grid causes visible broken-image
 * placeholders because the browser's per-domain connection limit (6) can't
 * fetch them all in time. Bulbapedia serves resized thumbnails at any
 * pre-generated width via the `/thumb/` path prefix.
 *
 * URL pattern:
 *   Full:  https://archives.bulbagarden.net/media/upload/{hash}/{file}.png
 *   Thumb: https://archives.bulbagarden.net/media/upload/thumb/{hash}/{file}.png/{width}px-{file}.png
 *
 * The DynamoDB `pack_art_urls` attribute stores full-size URLs (canonical).
 * This function derives thumbnail URLs at display time.
 */

const BULBA_UPLOAD_PREFIX =
  'https://archives.bulbagarden.net/media/upload/';

/**
 * Convert a full-size Bulbapedia image URL to a thumbnail at the given width.
 * Returns the original URL unchanged if it doesn't match the expected pattern.
 */
export function toBulbapediaThumbnail(
  fullUrl: string,
  width: number = 340,
): string {
  if (!fullUrl.startsWith(BULBA_UPLOAD_PREFIX)) return fullUrl;

  // Extract the path after the upload prefix: "{hash}/{filename}"
  // e.g., "8/8b/SV8_Booster_Pikachu.png"
  const relative = fullUrl.slice(BULBA_UPLOAD_PREFIX.length);
  const lastSlash = relative.lastIndexOf('/');
  if (lastSlash === -1) return fullUrl;

  const filename = relative.slice(lastSlash + 1);
  return `${BULBA_UPLOAD_PREFIX}thumb/${relative}/${width}px-${filename}`;
}
